'use client'

import { useRouter } from 'next/navigation'
import { useCallback, useMemo, useState } from 'react'

import { TERMS_TYPES, TERMS_TYPE_VALUES } from '../lib/constants'

import type { ITermsState, ITermItem } from './terms'
export interface ITermsActions {
  toggleTerm: (termKey: keyof ITermsState) => void
  toggleAll: () => void
  handleTermDetailClick: (termType: string) => void
  handleNoticeClick: () => void
  scrollToUncheckedTerm: () => void
}

interface IUseTermsReturn {
  state: ITermsState & {
    isAllAgreed: boolean
  }
  actions: ITermsActions
  termItems: ITermItem[]
}

/**
 * 약관 동의 상태 관리 커스텀 훅
 * 약관 항목 리스트, 상태, 핸들러를 모두 제공합니다.
 *
 * @example
 * ```tsx
 * const { state, actions, termItems } = useTerms()
 *
 * // 전체 동의
 * actions.toggleAll()
 *
 * // 개별 약관 동의
 * actions.toggleTerm('paymentServiceTerms')
 *
 * // 약관 상세보기
 * actions.handleTermDetailClick(termType)
 *
 * // 유의사항 전체보기
 * actions.handleNoticeClick()
 * ```
 */
const useTerms = (): IUseTermsReturn => {
  const router = useRouter()
  const [termsState, setTermsState] = useState<ITermsState>({
    paymentConfirmation: true,
    paymentServiceTerms: true,
    privacyPolicy: true,
    cancellationPolicy: true,
  })

  // 약관 항목 리스트
  const termItems: ITermItem[] = useMemo(
    () => [
      {
        key: 'paymentConfirmation',
        title: '상기 결제 내역 확인, 결제 진행에 동의',
        hasChevron: false,
      },
      {
        key: 'paymentServiceTerms',
        title: TERMS_TYPE_VALUES.PAYMENT_SERVICE_TERMS,
        hasChevron: true,
        termType: TERMS_TYPES.PAYMENT_SERVICE_TERMS,
      },
      {
        key: 'privacyPolicy',
        title: TERMS_TYPE_VALUES.PRIVACY_POLICY,
        hasChevron: true,
        termType: TERMS_TYPES.PRIVACY_POLICY,
      },
      {
        key: 'cancellationPolicy',
        title: TERMS_TYPE_VALUES.CANCELLATION_POLICY,
        hasChevron: true,
        termType: TERMS_TYPES.CANCELLATION_POLICY,
      },
    ],
    []
  )

  // 모든 약관 동의 여부 확인
  const isAllAgreed = useMemo(() => {
    return Object.values(termsState).every(value => value === true)
  }, [termsState])

  // 개별 약관 토글
  const toggleTerm = useCallback((termKey: keyof ITermsState) => {
    setTermsState(prev => ({
      ...prev,
      [termKey]: !prev[termKey],
    }))
  }, [])

  // 전체 동의 토글
  const toggleAll = useCallback(() => {
    const newValue = !isAllAgreed

    setTermsState({
      paymentConfirmation: newValue,
      paymentServiceTerms: newValue,
      privacyPolicy: newValue,
      cancellationPolicy: newValue,
    })
  }, [isAllAgreed])

  // 약관 상세보기 핸들러
  const handleTermDetailClick = useCallback(
    (termType: string) => {
      router.push(`/reservation/term/${termType}`)
    },
    [router]
  )

  // 유의사항 전체보기 핸들러
  const handleNoticeClick = useCallback(() => {
    router.push('/reservation/term/notice_all')
  }, [router])

  // 체크되지 않은 첫 번째 약관 항목으로 스크롤
  const scrollToUncheckedTerm = useCallback(() => {
    const termKeys: (keyof ITermsState)[] = [
      'paymentConfirmation',
      'paymentServiceTerms',
      'privacyPolicy',
      'cancellationPolicy',
    ]

    const uncheckedKey = termKeys.find(key => !termsState[key])
    if (uncheckedKey) {
      const element = document.getElementById(`payment-term-${uncheckedKey}`)
      if (element) {
        // on-site 레이아웃의 스크롤 컨테이너 또는 window 사용
        const scrollContainer = document.getElementById(
          'on-site-scroll-container'
        )

        if (scrollContainer) {
          const rect = element.getBoundingClientRect()
          const containerRect = scrollContainer.getBoundingClientRect()
          const relativeTop =
            rect.top - containerRect.top + scrollContainer.scrollTop
          const centerOffset =
            scrollContainer.clientHeight / 2 - rect.height / 2

          scrollContainer.scrollTo({
            top: relativeTop - centerOffset,
            behavior: 'smooth',
          })
        } else {
          // 스크롤 컨테이너가 없으면 scrollIntoView 사용
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    }
  }, [termsState])

  return {
    state: {
      ...termsState,
      isAllAgreed,
    },
    actions: {
      toggleTerm,
      toggleAll,
      handleTermDetailClick,
      handleNoticeClick,
      scrollToUncheckedTerm,
    },
    termItems,
  }
}

export default useTerms
